import './VideoPlayer.css';
import Hls from "hls.js";

import {useEffect, useRef, useState} from "react";

const hlsMimeTypes = [
    "application/x-mpegurl",
    "application/vnd.apple.mpegurl",
];
const nativeHlsMimeType = "application/vnd.apple.mpegurl";

function isHlsSource(source, type) {
    return hlsMimeTypes.includes(type?.toLowerCase()) || source.toLowerCase().includes(".m3u8");
}

function canUseNativeSource(video, source, type) {
    return !isHlsSource(source, type) || video.canPlayType(nativeHlsMimeType);
}

export default function VideoPlayer(props) {
    const videoRef = useRef(null);
    const [isInteracted, setIsInteracted] = useState(false);

    const {options, onReady} = props;

    useEffect(() => {
        const video = videoRef.current;
        const source = options.source;
        const type = options.type;

        if (!video || !source) {
            return undefined;
        }

        onReady && onReady(video);

        if (canUseNativeSource(video, source, type)) {
            video.src = source;
            return undefined;
        }

        if (!Hls.isSupported()) {
            console.error("HLS video is not supported in this browser.");
            return undefined;
        }

        const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 30,
        });
        hls.loadSource(source);
        hls.attachMedia(video);

        return () => {
            hls.destroy();
        };
    }, [options, onReady]);

    function handleEnded() {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        video.controls = true;
        if (document.fullscreenElement === video) {
            document.exitFullscreen();
        }
    }

    function handlePlay() {
        const video = videoRef.current;
        if (isInteracted) {
            return;
        }

        setIsInteracted(true)

        requestFullscreen()
    }

    function requestFullscreen() {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (video.requestFullscreen) {
            video.requestFullscreen();
        } else if (video.msRequestFullscreen) {
            video.msRequestFullscreen();
        } else if (video.mozRequestFullScreen) {
            video.mozRequestFullScreen();
        } else if (video.webkitRequestFullscreen) {
            video.webkitRequestFullscreen();
        }
    }

    return (
        <div className="video-player" data-vjs-player>
            <video
                className="video-player__video"
                ref={videoRef}
                autoPlay={options.autoplay ?? true}
                controls={options.controls ?? true}
                onEnded={handleEnded}
                onPlay={handlePlay}
                onClick={handlePlay}
                onDoubleClick={requestFullscreen}
            />
        </div>
    );
}
